import { useRouter } from '@uirouter/react';

import { Environment } from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { AccessDatatable } from '@/react/portainer/access-control/AccessManagement/AccessDatatable/AccessDatatable';
import {
  TeamAccessViewModel,
  UserAccessViewModel,
} from '@/portainer/models/access';

import { useAccesses } from '../queries/useAccesses';
import { useUpdateEnvironmentAccess } from '../queries/useUpdateEnvironmentAccess';

interface Props {
  environment: Environment;
  group: EnvironmentGroup;
}

export function AccessDatatableWrapper({ environment, group }: Props) {
  const router = useRouter();
  const { authorizedUsersAndTeams, policyID, policyName } = useAccesses(
    environment,
    group
  );
  const updateMutation = useUpdateEnvironmentAccess();

  if (!authorizedUsersAndTeams) {
    return null;
  }

  return (
    <AccessDatatable
      tableKey="access_endpoint"
      dataset={authorizedUsersAndTeams}
      onRemove={handleRemove}
      onUpdate={handleUpdate}
      showWarning
      isUpdateEnabled
      showRoles
      inheritFrom={!!group}
      policyId={policyID}
      policyName={policyName}
    />
  );

  async function handleRemove(
    selectedAccesses: Array<UserAccessViewModel | TeamAccessViewModel>
  ) {
    try {
      const newUserPolicies = { ...environment.UserAccessPolicies };
      const newTeamPolicies = { ...environment.TeamAccessPolicies };

      selectedAccesses.forEach((access) => {
        if (access.Type === 'user') {
          delete newUserPolicies[access.Id];
        } else {
          delete newTeamPolicies[access.Id];
        }
      });

      await updateMutation.mutateAsync({
        id: environment.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully updated');
      router.stateService.reload();
    } catch (error) {
      notifyError('Failed to update access', error as Error);
    }
  }

  async function handleUpdate(
    updatedUserAccesses: Array<UserAccessViewModel>,
    updatedTeamAccesses: Array<TeamAccessViewModel>
  ) {
    try {
      const newUserPolicies = { ...environment.UserAccessPolicies };
      const newTeamPolicies = { ...environment.TeamAccessPolicies };

      updatedUserAccesses.forEach((access) => {
        newUserPolicies[access.Id] = { RoleId: access.Role.Id };
      });

      updatedTeamAccesses.forEach((access) => {
        newTeamPolicies[access.Id] = { RoleId: access.Role.Id };
      });

      await updateMutation.mutateAsync({
        id: environment.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully updated');
      router.stateService.reload();
    } catch (error) {
      notifyError('Failed to update access', error as Error);
    }
  }
}
